import { useState, useEffect, useCallback } from 'react';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@gw/ui';
import { ApplicationSession } from '@gw/ui/types';
import { AlertCircle, Loader2, Coins } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { sessionsApi } from '@/api/sessions';
import type { ExtensionCostResponse, SessionBalanceResponse } from '@/api/sessions';

interface SessionExtensionModalProps {
  session: ApplicationSession;
  isOpen: boolean;
  onClose: () => void;
  onExtended?: () => void;
}

type ErrorCode = 'INSUFFICIENT_BALANCE' | 'SESSION_EXPIRED' | 'GENERIC';

function getErrorMessage(code: ErrorCode, detail?: string): string {
  switch (code) {
    case 'INSUFFICIENT_BALANCE':
      return 'Insufficient token balance to extend this session.';
    case 'SESSION_EXPIRED':
      return 'This session has already expired and cannot be extended.';
    default:
      return detail ?? 'Failed to extend session. Please try again.';
  }
}

/**
 * SessionExtensionModal — GW-2511
 *
 * Displays extension cost (from GET /sessions/{id}/extension-cost) and
 * current balance (from GET /sessions/{id}/balance), then executes
 * POST /sessions/{id}/extend with an idempotency key on confirm.
 */
export function SessionExtensionModal({
  session,
  isOpen,
  onClose,
  onExtended,
}: SessionExtensionModalProps) {
  const navigate = useNavigate();
  const [extensionCost, setExtensionCost] = useState<ExtensionCostResponse | null>(null);
  const [balance, setBalance] = useState<SessionBalanceResponse | null>(null);
  const [isLoadingCost, setIsLoadingCost] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [extendError, setExtendError] = useState<string | null>(null);

  const fetchCostAndBalance = useCallback(async () => {
    if (!session.id) return;

    setIsLoadingCost(true);
    setFetchError(null);
    setExtendError(null);

    try {
      const [costResult, balanceResult] = await Promise.all([
        sessionsApi.getExtensionCost(session.id),
        sessionsApi.getSessionBalance(session.id),
      ]);
      setExtensionCost(costResult);
      setBalance(balanceResult);
    } catch (error) {
      setFetchError(
        error instanceof Error ? error.message : 'Unable to load extension cost. Please try again.'
      );
    } finally {
      setIsLoadingCost(false);
    }
  }, [session.id]);

  useEffect(() => {
    if (isOpen) {
      fetchCostAndBalance();
    } else {
      // Reset on close
      setExtensionCost(null);
      setBalance(null);
      setFetchError(null);
      setExtendError(null);
    }
  }, [isOpen, fetchCostAndBalance]);

  const handleExtend = async () => {
    if (!extensionCost || !balance) return;

    const idempotencyKey = crypto.randomUUID();
    setIsExtending(true);
    setExtendError(null);

    try {
      await sessionsApi.extendSession(session.id, idempotencyKey);
      toast.success(`Session extended by ${extensionCost.durationMinutes} minutes`);
      onClose();
      onExtended?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      let code: ErrorCode = 'GENERIC';
      if (message.includes('INSUFFICIENT') || message.includes('insufficient')) {
        code = 'INSUFFICIENT_BALANCE';
      } else if (message.includes('EXPIRED') || message.includes('expired')) {
        code = 'SESSION_EXPIRED';
      }
      setExtendError(getErrorMessage(code, message || undefined));
    } finally {
      setIsExtending(false);
    }
  };

  const costTokens = extensionCost?.extensionCostTokens ?? 0;
  const balanceTokens = balance?.balanceTokens ?? 0;
  const hasInsufficientBalance = balance !== null && balanceTokens < costTokens;
  const balanceAfter = balanceTokens - costTokens;
  const canConfirm = !isLoadingCost && !fetchError && !hasInsufficientBalance && !isExtending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="session-extension-modal">
        <DialogHeader>
          <DialogTitle>Extend Session</DialogTitle>
          <DialogDescription>
            Extend your active session by purchasing additional time.
          </DialogDescription>
        </DialogHeader>

        {/* Loading state */}
        {isLoadingCost && (
          <div className="flex items-center justify-center py-8" data-testid="loading-state">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            <span className="text-muted-foreground ml-2 text-sm">Loading extension cost...</span>
          </div>
        )}

        {/* Fetch error */}
        {fetchError && !isLoadingCost && (
          <Alert variant="destructive" data-testid="fetch-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{fetchError}</AlertDescription>
          </Alert>
        )}

        {/* Cost and balance summary */}
        {!isLoadingCost && !fetchError && extensionCost && balance && (
          <div className="space-y-4">
            <div className="bg-muted space-y-3 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Extension Duration</span>
                <span
                  className="font-mono font-semibold tabular-nums"
                  data-testid="extension-duration"
                >
                  {extensionCost.durationMinutes} minutes
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Extension Cost</span>
                <span
                  className="font-mono text-lg font-bold tabular-nums"
                  data-testid="extension-cost"
                >
                  {costTokens.toLocaleString()} tokens
                </span>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Current Balance</span>
                <span className="font-mono font-medium tabular-nums" data-testid="current-balance">
                  {balanceTokens.toLocaleString()} tokens
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Balance After Extension</span>
                <span
                  className={`font-mono font-medium tabular-nums ${
                    hasInsufficientBalance ? 'text-destructive' : 'text-success'
                  }`}
                  data-testid="balance-after"
                >
                  {balanceAfter.toLocaleString()} tokens
                </span>
              </div>
            </div>

            {/* Insufficient balance warning */}
            {hasInsufficientBalance && (
              <Alert variant="destructive" data-testid="insufficient-balance-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex flex-col gap-2">
                  <span>
                    Insufficient balance. You need {(costTokens - balanceTokens).toLocaleString()}{' '}
                    more tokens to extend this session.
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    data-testid="buy-tokens-button"
                    onClick={() => {
                      onClose();
                      navigate('/organization/tokens');
                    }}
                  >
                    <Coins className="mr-1 h-4 w-4" />
                    Buy Tokens
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Extension error */}
            {extendError && (
              <Alert variant="destructive" data-testid="extend-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{extendError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isExtending}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button onClick={handleExtend} disabled={!canConfirm} data-testid="confirm-button">
            {isExtending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Extending...
              </>
            ) : (
              'Confirm Extension'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
